import Vue from 'vue';
import router from './router';
import App from './App.vue';
import CustomQuillEditor from './plugins/quill';
import Default from './components/layout/Default.vue';
import NoLayout from './components/layout/NoLayout.vue';
import BootstrapVue from 'bootstrap-vue';
import Vuelidate from 'vuelidate';
import moment from 'moment';
import VueMoment from 'vue-moment';
import VueSanitize from 'vue-sanitize';
import store from './store/index.js';
import constants from './constants.js';
import * as msal from '@azure/msal-browser';
import { msalConfig } from '../azureMSAuthenConfig/authConfig';
import VueCookies from 'vue-cookies';
import VueElementLoading from 'vue-element-loading';
import VueToast from 'vue-toast-notification';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import solidIcons from '@/assets/free-solid-svg-icon-list.js';
import regularIcons from '@/assets/free-regular-svg-icon-list.js';
import 'vue-toast-notification/dist/theme-sugar.css';
import infiniteScroll from 'vue-infinite-scroll';

import VueHead from 'vue-head';
import { initializeApp } from 'firebase/app';
import firebaseConfig from './configs/firebase-config';
import { amplitudePlugin } from './plugins/amplitude';
import { globalMixin } from '@/mixins';
// import * as SendBird from "sendbird";
import VLazyImage from 'v-lazy-image/v2';
import VueSelect from 'vue-select';
import sentryPlugin from './plugins/sentry';
import getSiteControlPlugin from './plugins/getsitecontrol';
import LogRocket from 'logrocket';
import optimizelyPlugin from './plugins/optimizely';
import optimizelySdk from '@optimizely/optimizely-sdk';
import VueGtm from '@gtm-support/vue2-gtm';
import ConfigPlugin from './plugins/config';
import appConfig from './configs/app-config';
import newRelicPlugin from './plugins/new-relic';
import DomPurifyPlugin from './plugins/dompurify';
import VueOwlCarousel from 'vue-owl-carousel';

export const eventBus = new Vue();

const init = async () => {
  if (process.env.VUE_APP_NEW_RELIC_ENABLED === 'true') {
    Vue.use(newRelicPlugin, {
      accountID: `${process.env.VUE_APP_NEW_RELIC_ACCOUNT_ID}`,
      applicationID: `${process.env.VUE_APP_NEW_RELIC_APP_ID}`,
      licenseKey: `${process.env.VUE_APP_NEW_RELIC_LICENSE_KEY}`
    });
  }

  if (process.env.VUE_APP_SENTRY_ENABLED === 'true' && process.env.VUE_APP_SENTRY_DSN) {
    let tracingOrigins;

    if (process.env.VUE_APP_SENTRY_TRACING_ORIGINS) {
      tracingOrigins = [...process.env.VUE_APP_SENTRY_TRACING_ORIGINS.split(',').filter(Boolean)];
    }

    Vue.use(sentryPlugin, router, process.env.VUE_APP_SENTRY_DSN, {
      environment: process.env.VUE_APP_SENTRY_ENVIRONMENT,

      tracingOrigins,

      tracesSampleRate: () => {
        const sampleRateConfig = process.env.VUE_APP_SENTRY_SAMPLE_RATE;

        return sampleRateConfig && sampleRateConfig.length ? parseFloat(sampleRateConfig) : 1.0;
      }
    });
  }

  Vue.use(getSiteControlPlugin, {
    initialParams: {
      env: process.env.VUE_APP_GSC_ENV || 'local'
    },
    widgets: {
      learningFeedSurvey: 407624
    }
  });

  Vue.use(
    amplitudePlugin,
    process.env.VUE_APP_AMPLITUDE_API_KEY,
    process.env.VUE_APP_AMPLITUDE_BRANCH
  );

  if (process.env.VUE_APP_LOG_ROCKET_ENABLED === 'true' && process.env.VUE_APP_LOG_ROCKET_PROJECT) {
    Vue.prototype.$logrocket = LogRocket;
  }

  initializeApp(firebaseConfig);

  const msalInstance = new msal.PublicClientApplication(msalConfig);
  // const sb = new SendBird({ appId: process.env.VUE_APP_SENDBIRD_APP_ID });
  const sanitizeHtmlOptions = {
    allowedTags: [],
    allowedAttributes: {}
  };

  const optimizely = optimizelySdk.createInstance({
    sdkKey: process.env.VUE_APP_OPTIMIZELY_SDK
  });

  await optimizely.onReady().then(({ success, reason }) => {
    console.log('client optimizely is ready');
    if (!success) {
      console.log(`client initialization unsuccessful, reason: ${reason}`);
      return;
    }
  });

  Vue.use(BootstrapVue);
  Vue.use(Vuelidate);
  Vue.use(VueCookies);
  Vue.use(VueMoment, { moment });
  Vue.use(VueToast);
  Vue.use(VueSanitize, sanitizeHtmlOptions);
  Vue.use(VueHead);
  Vue.use(optimizelyPlugin, optimizely);
  Vue.use(infiniteScroll);
  Vue.use(ConfigPlugin, appConfig);
  Vue.use(DomPurifyPlugin);

  if (process.env.VUE_APP_GTM_ENABLED === 'true') {
    if (!process.env.VUE_APP_GTM_ID) {
      throw new Error("env 'VUE_APP_GTM_ID' is required.");
    }

    Vue.use(VueGtm, {
      id: process.env.VUE_APP_GTM_ID,
      enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
      loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
      vueRouter: router // Pass the router instance to automatically sync with router (optional)
    });
  }

  Vue.prototype.$constants = constants;
  Vue.prototype.$msalInstance = msalInstance;
  Vue.prototype.$firebaseRecaptchaSiteKey = process.env.VUE_APP_FIREBASE_RECAPTCHA_SITE_KEY;

  Vue.component('custom-quill-editor', CustomQuillEditor);
  Vue.component('v-select', VueSelect);
  Vue.component('VueElementLoading', VueElementLoading);
  Vue.component('default-layout', Default);
  Vue.component('no-layout', NoLayout);
  Vue.component('font-awesome-icon', FontAwesomeIcon);
  Vue.component('v-lazy-image', VLazyImage);
  Vue.component('VueOwlCarousel', VueOwlCarousel);

  library.add(solidIcons);
  library.add(regularIcons);

  Vue.config.productionTip = false;

  Vue.mixin(globalMixin);

  new Vue({
    store,
    router,
    render: (h) => h(App)
  }).$mount('#app');
};

init();
